var _a, _b, _c;
import { isEmptyObject } from '@philips-internal/oswald-ui-utils';
const HASHCODE = Symbol('hashCode');
const PROPS_DEFINITION = Symbol('propsDefinition');
const STATE = Symbol('state');
const DEFAULT_STATE = Symbol('defaultState');
const META_PROPS = Symbol('metaProps');
const PROPS = Symbol('props');
const VOLATILE_STATE = Symbol('volatileState');
const REFS = Symbol('refs');
const PROPS_SUBSCRIBERS = Symbol('props-subscribers');
const STATE_SUBSCRIBERS = Symbol('state-subscribers');
function cleanObject(data) {
    for (const key in data) {
        if (data[key] === undefined) {
            delete data[key];
        }
    }
}
function subscribe(target, callback) {
    if (target.has(callback)) {
        throw new Error('[Single Source of Truth]: Listener already added.');
    }
    target.add(callback);
    return () => {
        target.delete(callback);
    };
}
class SingleSourceOfTruth {
    constructor(hashCode, defaultState = {}, propsDefinition = []) {
        this[_a] = new Set();
        this[_b] = new Set();
        this[_c] = {};
        this[HASHCODE] = hashCode;
        this[PROPS_DEFINITION] = propsDefinition;
        this[STATE] = {
            [DEFAULT_STATE]: Object.freeze(defaultState),
            [META_PROPS]: {},
            [PROPS]: {},
            [VOLATILE_STATE]: {},
        };
    }
    get refs() {
        return this[REFS];
    }
    get state() {
        const state = this[STATE];
        const defaultState = state[DEFAULT_STATE];
        const metaProps = state[META_PROPS];
        const props = state[PROPS];
        const volatileState = state[VOLATILE_STATE];
        return {
            ...defaultState,
            ...metaProps,
            ...props,
            ...volatileState,
        };
    }
    get defaultState() {
        return { ...this[STATE][DEFAULT_STATE] };
    }
    get metaProps() {
        return { ...this[STATE][META_PROPS] };
    }
    setMetaProps(props = {}) {
        this[STATE][META_PROPS] = props;
    }
    get props() {
        return { ...this[STATE][PROPS] };
    }
    setProps(props = {}) {
        this[STATE][PROPS] = props;
    }
    get propsDefinition() {
        return this[PROPS_DEFINITION];
    }
    get volatileState() {
        return { ...this[STATE][VOLATILE_STATE] };
    }
    get hashCode() {
        return this[HASHCODE];
    }
    /**
     * Clean objects modified during the data update cycle.
     */
    cleanUp() {
        cleanObject(this[STATE][PROPS]);
        cleanObject(this[STATE][VOLATILE_STATE]);
    }
    /**
     * Apply new data to the SSOT, assigning the data to the correct target object (state, props).
     *
     * @param data - Data that should be applied.
     */
    apply(data) {
        if (isEmptyObject(data)) {
            return { props: {}, state: {} };
        }
        const baseProps = {
            ...this.defaultState,
            ...this.metaProps,
            ...this.props,
        };
        const newProps = {};
        const newState = {};
        for (const key of Reflect.ownKeys(data)) {
            const isProperty = this.propsDefinition.includes(key);
            // Accessing these variables like this because we don't want to expose a setter for them.
            const targetObject = isProperty
                ? this[STATE][PROPS]
                : this[STATE][VOLATILE_STATE];
            if (baseProps[key] !== data[key]) {
                targetObject[key] = data[key];
            }
            const mapObj = isProperty
                ? newProps
                : newState;
            mapObj[key] = data[key];
        }
        this.cleanUp();
        return { props: newProps, state: newState };
    }
    /**
     * Apply new data to the SSOT, assigning the data to the correct target object (state, props), and
     * notify the necessary listeners based on the result of the data update.
     *
     * @param data - Data that should be applied.
     */
    applyAndNotify(data) {
        const { props, state } = this.apply(data);
        if (!isEmptyObject(props)) {
            this.notifyProps(props);
        }
        if (!isEmptyObject(state)) {
            this.notifyState(state);
        }
    }
    /**
     * Add a new object reference to the SSOT.
     *
     * @param name - Reference name;
     * @param ref - Reference object.
     */
    setRef(name, ref) {
        this[REFS][name] = ref;
    }
    /**
     * Removes an object reference from the SSOT.
     *
     * @param name - Reference name.
     */
    removeRef(name) {
        delete this[REFS][name];
    }
    /**
     * Add a subscription to SSOT props updates.
     *
     * @param callback - Callback to be called when the props object changes.
     */
    subscribeToProps(callback) {
        return subscribe(this[PROPS_SUBSCRIBERS], callback);
    }
    /**
     * Notify subscribers to SSOT props updates.
     *
     * @param props - The updated props object.
     */
    notifyProps(props) {
        for (const callback of this[PROPS_SUBSCRIBERS]) {
            callback(props);
        }
    }
    /**
     * Add a subscription to SSOT state updates.
     *
     * @param callback - Callback to be called when the state object changes.
     */
    subscribeToState(callback) {
        return subscribe(this[STATE_SUBSCRIBERS], callback);
    }
    /**
     * Notify subscribers to SSOT state updates.
     *
     * @param state - The updated state object.
     */
    notifyState(state) {
        for (const callback of this[STATE_SUBSCRIBERS]) {
            callback(state);
        }
    }
}
_a = PROPS_SUBSCRIBERS, _b = STATE_SUBSCRIBERS, _c = REFS;
export { SingleSourceOfTruth, };
//# sourceMappingURL=single-source-of-truth.js.map